import {
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLL_STRATEGY,
  VirtualScrollStrategy,
} from '@angular/cdk/scrolling';
import {
  Directive,
  forwardRef,
  inject,
  InjectionToken,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export class ColumnAutosizeVirtualScrollStrategy implements VirtualScrollStrategy {
  private readonly _scrolledIndexChange = new Subject<number>();

  private viewport: CdkVirtualScrollViewport | null = null;
  private itemSizes: number[] = [];
  private minBufferPx: number;
  private maxBufferPx: number;

  scrolledIndexChange: Observable<number> = this._scrolledIndexChange.pipe(distinctUntilChanged());

  constructor(itemSizes: number[], minBufferPx: number, maxBufferPx: number) {
    this.itemSizes = itemSizes;
    this.minBufferPx = minBufferPx;
    this.maxBufferPx = maxBufferPx;
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.updateTotalContentSize();
    this.updateRenderedRange();
  }

  detach(): void {
    this._scrolledIndexChange.complete();
    this.viewport = null;
  }

  resetViewportSize(): void {
    if (this.viewport) {
      this.viewport.checkViewportSize();
      this.updateTotalContentSize();
      this.updateRenderedRange();
    }
  }

  updateItemAndBufferSize(itemSizes: number[], minBufferPx: number, maxBufferPx: number): void {
    this.itemSizes = itemSizes;
    this.minBufferPx = minBufferPx;
    this.maxBufferPx = maxBufferPx;

    this.updateTotalContentSize();
    this.updateRenderedRange();
  }

  onContentScrolled(): void {
    this.updateRenderedRange();
  }

  onDataLengthChanged(): void {
    this.updateTotalContentSize();
    this.updateRenderedRange();
  }

  /** @docs-private Implemented as part of VirtualScrollStrategy. */
  onContentRendered(): void {
    /* no-op */
  }

  /** @docs-private Implemented as part of VirtualScrollStrategy. */
  onRenderedOffsetChanged(): void {
    /* no-op */
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this.viewport) {
      return;
    }

    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += this.itemSizes[i];
    }

    this.viewport.scrollToOffset(offset, behavior);
  }

  private updateTotalContentSize(): void {
    if (!this.viewport) {
      return;
    }

    this.viewport.setTotalContentSize(
      this.itemSizes.reduce((totalSize, itemSize) => totalSize + itemSize, 0),
    );
  }

  private updateRenderedRange(): void {
    if (!this.viewport || this.itemSizes.length === 0) {
      return;
    }

    const renderedRange = this.viewport.getRenderedRange();
    const newRange = { start: renderedRange.start, end: renderedRange.end };

    const viewportSize = this.viewport.getViewportSize();
    const dataLength = this.viewport.getDataLength();
    const scrollOffset = this.viewport.measureScrollOffset();

    const firstVisibleIndex = this.getFirstVisibleIndex(scrollOffset);

    const startBuffer = Math.max(0, scrollOffset - this.getOffset(newRange.start));
    const endBuffer = this.getOffset(newRange.end) - scrollOffset - viewportSize;

    if (
      (startBuffer < this.minBufferPx && newRange.start !== 0) ||
      (endBuffer < this.minBufferPx && newRange.end !== dataLength)
    ) {
      newRange.start = Math.max(0, this.getFirstVisibleIndex(scrollOffset - this.maxBufferPx) - 1);
      newRange.end = Math.min(
        dataLength,
        this.getFirstVisibleIndex(viewportSize + scrollOffset + this.maxBufferPx),
      );
    }

    this.viewport.setRenderedRange(newRange);
    this.viewport.setRenderedContentOffset(this.getOffset(newRange.start));
    this._scrolledIndexChange.next(Math.floor(firstVisibleIndex));
  }

  private getOffset(index: number): number {
    return this.itemSizes.slice(0, index).reduce((sum, size) => sum + size, 0);
  }

  private getFirstVisibleIndex(scrollOffset: number, startFrom = 0): number {
    let offset = 0;
    let index = startFrom;
    while (offset < scrollOffset) {
      offset += this.itemSizes[index++];
    }

    return index;
  }
}

function columnAutosizeStrategyFactory(
  directive: ColumnAutosizeVirtualScrollDirective,
): ColumnAutosizeVirtualScrollStrategy {
  return directive._scrollStrategy;
}

export const HOST_ELEMENT = new InjectionToken('VIRTUAL_SCROLL_RESIZE_HOST_ELEMENT');

@UntilDestroy()
@Directive({
  selector: '[blazeColumnAutosize]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: columnAutosizeStrategyFactory,
      deps: [forwardRef(() => ColumnAutosizeVirtualScrollDirective)],
    },
  ],
  standalone: false,
})
export class ColumnAutosizeVirtualScrollDirective implements OnChanges {
  private hostElement = inject<HTMLElement>(HOST_ELEMENT, { optional: true });

  @Input() itemSizes: number[] = [];
  @Input() minBufferPx = 50;
  @Input() maxBufferPx = 200;

  _scrollStrategy = new ColumnAutosizeVirtualScrollStrategy(
    this.itemSizes,
    this.minBufferPx,
    this.maxBufferPx,
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minBufferPx'] || changes['maxBufferPx'] || changes['itemSizes']) {
      this._scrollStrategy.updateItemAndBufferSize(
        this.itemSizes,
        this.minBufferPx,
        this.maxBufferPx,
      );
    }
  }

  resetViewportSize(): void {
    this._scrollStrategy.resetViewportSize();
  }
}
